import { Calendar, Clock, ArrowRight } from 'lucide-react'
import Section from '../components/Section'
import Button from '../components/Button'
import Card from '../components/Card'

const blogPosts = [
    {
        title: 'The Rise of Agentic Productivity: Why 2025 Changes Everything',
        excerpt: 'Traditional to-do apps are becoming obsolete. Here\'s how AI agents are transforming the productivity landscape.',
        category: 'Industry Insights',
        date: 'January 3, 2026',
        readTime: '8 min read',
        featured: true,
    },
    {
        title: 'Voice-First Capture: The Science Behind Why It Works',
        excerpt: 'Research shows voice capture reduces cognitive load by 40%. We break down the neuroscience behind this revolutionary approach.',
        category: 'Research',
        date: 'December 28, 2025',
        readTime: '6 min read',
        featured: false,
    },
    {
        title: 'ADHD and Productivity: How Way2Will Becomes Your External Brain',
        excerpt: 'Designed with neurodiversity in mind, Way2Will acts as an executive function support system. Here\'s how.',
        category: 'Neurodiversity',
        date: 'December 20, 2025',
        readTime: '10 min read',
        featured: false,
    },
    {
        title: 'From Motion to Way2Will: A User\'s Migration Story',
        excerpt: 'One founder shares their journey from rigid scheduling to flexible AI-powered productivity.',
        category: 'User Stories',
        date: 'December 15, 2025',
        readTime: '5 min read',
        featured: false,
    },
    {
        title: 'Building Your Second Brain: The Chief of Staff Memory System',
        excerpt: 'How vector databases and RAG technology give Way2Will perfect recall of your past conversations.',
        category: 'Deep Dives',
        date: 'December 10, 2025',
        readTime: '12 min read',
        featured: false,
    },
    {
        title: '10 Voice Commands That Will Change How You Work',
        excerpt: 'Master these verbal shortcuts to capture ideas, schedule meetings, and manage projects instantly.',
        category: 'Tips & Tricks',
        date: 'December 5, 2025',
        readTime: '4 min read',
        featured: false,
    },
]

const categories = ['All', 'Industry Insights', 'Research', 'Neurodiversity', 'User Stories', 'Deep Dives', 'Tips & Tricks']

export default function Blog() {
    const featuredPost = blogPosts.find(post => post.featured)
    const regularPosts = blogPosts.filter(post => !post.featured)

    return (
        <>
            {/* Hero Section */}
            <Section className="pt-12 lg:pt-20">
                <div className="text-center max-w-4xl mx-auto">
                    <h1 className="text-4xl lg:text-5xl font-bold text-primary-color leading-tight mb-6">
                        The Way2Will Blog
                    </h1>
                    <p className="text-lg lg:text-xl font-normal text-secondary-color leading-relaxed">
                        Insights on productivity, neurodiversity, AI, and the future of work.
                    </p>
                </div>
            </Section>

            {/* Categories */}
            <Section className="py-8">
                <div className="flex flex-wrap justify-center gap-3">
                    {categories.map((category) => (
                        <button
                            key={category}
                            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${category === 'All'
                                ? 'bg-primary-base text-white/85'
                                : 'glass text-secondary-color hover:text-primary-color'
                                }`}
                        >
                            {category}
                        </button>
                    ))}
                </div>
            </Section>

            {/* Featured Post */}
            {featuredPost && (
                <Section>
                    <Card variant="elevated" className="p-0 overflow-hidden">
                        <div className="grid grid-cols-1 lg:grid-cols-2">
                            <div className="bg-gradient-to-br from-primary-base/20 to-accent-cyan/20 min-h-[300px] flex items-center justify-center">
                                <div className="w-24 h-24 rounded-full gradient-primary flex items-center justify-center">
                                    <span className="text-3xl">📰</span>
                                </div>
                            </div>
                            <div className="p-8 lg:p-12 flex flex-col justify-center">
                                <div className="flex items-center gap-4 mb-4">
                                    <span className="text-xs font-semibold text-white bg-primary-base px-2 py-1 rounded-full">
                                        Featured
                                    </span>
                                    <span className="text-sm text-primary-base font-medium">{featuredPost.category}</span>
                                </div>
                                <h2 className="text-2xl lg:text-3xl font-semibold text-primary-color mb-4">
                                    {featuredPost.title}
                                </h2>
                                <p className="text-secondary-color mb-6">{featuredPost.excerpt}</p>
                                <div className="flex items-center gap-6 mb-6">
                                    <div className="flex items-center gap-2 text-sm text-tertiary-color">
                                        <Calendar className="w-4 h-4" />
                                        {featuredPost.date}
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-tertiary-color">
                                        <Clock className="w-4 h-4" />
                                        {featuredPost.readTime}
                                    </div>
                                </div>
                                <Button className="self-start">
                                    Read Article <ArrowRight className="w-4 h-4 ml-2" />
                                </Button>
                            </div>
                        </div>
                    </Card>
                </Section>
            )}

            {/* Blog Posts Grid */}
            <Section>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {regularPosts.map((post, index) => (
                        <Card key={index} variant="default" className="flex flex-col p-0 overflow-hidden">
                            <div className="bg-gradient-to-br from-primary-base/10 to-accent-cyan/10 h-48 flex items-center justify-center">
                                <div className="w-16 h-16 rounded-full gradient-primary flex items-center justify-center opacity-60">
                                    <span className="text-xl">📝</span>
                                </div>
                            </div>
                            <div className="p-6 flex flex-col flex-1">
                                <span className="text-xs font-semibold text-primary-base mb-2">{post.category}</span>
                                <h3 className="text-lg font-semibold text-primary-color mb-3 flex-1">
                                    {post.title}
                                </h3>
                                <p className="text-sm text-secondary-color mb-4">{post.excerpt}</p>
                                <div className="flex items-center justify-between mt-auto pt-4 border-t border-primary-base/10">
                                    <div className="flex items-center gap-4 text-xs text-tertiary-color">
                                        <span>{post.date}</span>
                                        <span>{post.readTime}</span>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-primary-base" />
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>

                <div className="text-center mt-12">
                    <Button variant="secondary" size="lg">Load More Articles</Button>
                </div>
            </Section>

            {/* Newsletter CTA */}
            <Section glass>
                <div className="max-w-2xl mx-auto text-center">
                    <h2 className="text-2xl lg:text-3xl font-semibold text-primary-color mb-4">
                        Stay in the Loop
                    </h2>
                    <p className="text-secondary-color mb-8">
                        Get weekly insights on productivity, AI, and building better workflows. No spam, unsubscribe anytime.
                    </p>
                    <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-6 py-3 glass rounded-full focus:outline-none focus:border-primary-base/25"
                        />
                        <Button type="submit">Subscribe</Button>
                    </form>
                </div>
            </Section>
        </>
    )
}
